import { createContext, useContext } from 'react';
import type { SxProps, Theme } from '@mui/material';
import type { SnackbarVariant } from '@/components/atoms/Snackbar/Snackbar.props';

export interface ShowSnackbarOptions {
  /**
   * Variant determines the color scheme and icon
   * @default 'info'
   */
  variant?: SnackbarVariant;
  /**
   * Auto-hide duration in milliseconds
   * @default 4000
   */
  autoHideDuration?: number | null;
  /**
   * Anchor origin for snackbar position
   * @default { vertical: 'bottom', horizontal: 'center' }
   */
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  /**
   * Custom sx styles
   */
  sx?: SxProps<Theme>;
}

export interface SnackbarItem {
  id: number;
  open: boolean;
  message: string;
  variant: SnackbarVariant;
  autoHideDuration: number | null;
  anchorOrigin: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  sx?: SxProps<Theme>;
}

export interface SnackbarContextValue {
  showSnackbar: (message: string, options?: ShowSnackbarOptions) => void;
}

export const SnackbarContext = createContext<SnackbarContextValue>({
  showSnackbar: () => {
    console.warn('SnackbarContext not initialized');
  },
});

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
